/**
 * SwapProviderManager - Hot-swappable provider management
 *
 * Manages multiple swap providers and allows switching between them at runtime.
 * Provides a unified interface for quote fetching and swap execution.
 */

import { JupiterSwapProvider } from './JupiterSwapProvider.js';
import { TitanSwapProvider } from './TitanSwapProvider.js';

// Default RPC endpoints
const DEFAULT_RPC_ENDPOINT = 'https://solanac-solana-c8d3.mainnet.rpcpool.com/39701859-d2f9-40e5-93bb-491f5a4791e1';
const DEFAULT_HELIUS_ENDPOINT = 'https://specific-lanny-fast-mainnet.helius-rpc.com/';

export class SwapProviderManager {
    constructor(config = {}) {
        this.config = config;
        this._providers = new Map();
        this._activeProvider = null;
        this._activeProviderName = null;
        this._callbacks = {
            quote: [],
            error: [],
            status: [],
            providerChanged: []
        };
        this._lastQuoteParams = null;
    }

    /**
     * Register default providers
     */
    async registerDefaults() {
        // Get config from window.swapConfig (set by Laravel) or fallback to constructor config
        const swapConfig = (typeof window !== 'undefined' && window.swapConfig) || {};
        const jupiterConfig = swapConfig.jupiter || {};

        // Register Jupiter (default)
        await this.registerProvider('jupiter', JupiterSwapProvider, {
            endpoint: this.config.rpcEndpoint || swapConfig.rpcEndpoint || DEFAULT_HELIUS_ENDPOINT,
            apiKey: this.config.jupiterApiKey || jupiterConfig.apiKey || null,
            feeAccount: this.config.jupiterFeeAccount || jupiterConfig.feeAccount || null,
            referralAccount: this.config.jupiterReferralAccount || jupiterConfig.referralAccount || null,
            feeBps: this.config.jupiterFeeBps ?? jupiterConfig.feeBps ?? 0,
            pollInterval: this.config.pollInterval || 3000
        });

        // Register Titan
        await this.registerProvider('titan', TitanSwapProvider, {
            rpcEndpoint: this.config.rpcEndpoint || DEFAULT_RPC_ENDPOINT,
            authToken: this.config.titanAuthToken || null
        });

        // Set default active provider
        if (!this._activeProviderName) {
            await this.setActiveProvider('jupiter');
        }
    }

    /**
     * Register a new provider
     */
    async registerProvider(name, ProviderClass, config = {}) {
        try {
            const provider = new ProviderClass(config);

            // Set up event forwarding
            provider.onQuote(quotes => this._onProviderQuote(name, quotes));
            provider.onError(error => this._onProviderError(name, error));
            provider.onStatus((status, message) => this._onProviderStatus(name, status, message));

            this._providers.set(name, provider);

            return true;
        } catch (error) {
            console.error(`Failed to register provider ${name}:`, error);
            return false;
        }
    }

    /**
     * Initialize a specific provider
     */
    async initializeProvider(name) {
        const provider = this._providers.get(name);
        if (!provider) {
            throw new Error(`Provider ${name} not registered`);
        }

        if (!provider.isConnected) {
            await provider.initialize();
        }

        return provider.isConnected;
    }

    /**
     * Get list of available providers
     */
    getAvailableProviders() {
        const providers = [];
        for (const [name, provider] of this._providers) {
            providers.push({
                name,
                ...provider.getInfo()
            });
        }
        return providers;
    }

    /**
     * Get the active provider
     */
    getActiveProvider() {
        return this._activeProvider;
    }

    /**
     * Get active provider name
     */
    getActiveProviderName() {
        return this._activeProviderName;
    }

    /**
     * Set the active provider
     */
    async setActiveProvider(name) {
        const provider = this._providers.get(name);
        if (!provider) {
            throw new Error(`Provider ${name} not registered`);
        }

        // Stop streaming on current provider
        if (this._activeProvider && this._activeProvider.isStreaming) {
            this._activeProvider.stopQuoteStream();
        }

        // Initialize new provider if needed
        if (!provider.isConnected) {
            await provider.initialize();
        }

        this._activeProvider = provider;
        this._activeProviderName = name;

        // Emit provider changed event
        this._emit('providerChanged', { name, provider: provider.getInfo() });

        // Resume streaming with last params if we had any
        if (this._lastQuoteParams) {
            this.startQuoteStream(this._lastQuoteParams);
        }

        return true;
    }

    /**
     * Get a quote from the active provider
     */
    async getQuote(params) {
        if (!this._activeProvider) {
            throw new Error('No active provider set');
        }

        this._lastQuoteParams = params;
        return this._activeProvider.getQuote(params);
    }

    /**
     * Start streaming quotes from the active provider
     */
    startQuoteStream(params) {
        if (!this._activeProvider) {
            throw new Error('No active provider set');
        }

        this._lastQuoteParams = params;
        return this._activeProvider.startQuoteStream(params);
    }

    /**
     * Stop streaming quotes
     */
    stopQuoteStream() {
        if (this._activeProvider) {
            this._activeProvider.stopQuoteStream();
        }
        this._lastQuoteParams = null;
    }

    /**
     * Update streaming parameters
     */
    updateStreamParams(params) {
        this._lastQuoteParams = { ...this._lastQuoteParams, ...params };
        if (this._activeProvider && this._activeProvider.updateStreamParams) {
            this._activeProvider.updateStreamParams(params);
        }
    }

    /**
     * Execute a swap
     */
    async executeSwap(quote, wallet) {
        // Find the provider that generated this quote
        const providerName = quote.provider.split(':')[0]; // Handle 'titan:providerId' format
        let provider = this._providers.get(providerName);

        // Fallback to active provider
        if (!provider) {
            provider = this._activeProvider;
        }

        if (!provider) {
            throw new Error('No provider available for swap execution');
        }

        return provider.executeSwap(quote, wallet);
    }

    /**
     * Get quotes from all providers simultaneously
     */
    async getQuotesFromAll(params) {
        const results = [];
        const errors = [];

        const promises = [];
        for (const [name, provider] of this._providers) {
            if (!provider.isConnected) {
                try {
                    await provider.initialize();
                } catch (e) {
                    errors.push({ provider: name, error: e });
                    continue;
                }
            }

            promises.push(
                provider.getQuote(params)
                    .then(quotes => ({ provider: name, quotes }))
                    .catch(error => {
                        errors.push({ provider: name, error });
                        return null;
                    })
            );
        }

        const settled = await Promise.all(promises);

        for (const result of settled) {
            if (result && result.quotes) {
                results.push(...result.quotes);
            }
        }

        // Sort by output amount (best first)
        results.sort((a, b) => b.outputAmount - a.outputAmount);

        return {
            quotes: results,
            errors
        };
    }

    /**
     * Subscribe to quote updates
     */
    onQuote(callback) {
        this._callbacks.quote.push(callback);
    }

    /**
     * Subscribe to errors
     */
    onError(callback) {
        this._callbacks.error.push(callback);
    }

    /**
     * Subscribe to status changes
     */
    onStatus(callback) {
        this._callbacks.status.push(callback);
    }

    /**
     * Subscribe to provider changes
     */
    onProviderChanged(callback) {
        this._callbacks.providerChanged.push(callback);
    }

    /**
     * Clear all callbacks
     */
    clearCallbacks() {
        this._callbacks = {
            quote: [],
            error: [],
            status: [],
            providerChanged: []
        };
    }

    /**
     * Internal: handle quote from provider
     */
    _onProviderQuote(providerName, quotes) {
        // Only forward if from active provider
        if (providerName === this._activeProviderName) {
            this._emit('quote', quotes);
        }
    }

    /**
     * Internal: handle error from provider
     */
    _onProviderError(providerName, error) {
        this._emit('error', { provider: providerName, error });
    }

    /**
     * Internal: handle status from provider
     */
    _onProviderStatus(providerName, status) {
        this._emit('status', { provider: providerName, ...status });
    }

    /**
     * Internal: emit event to subscribers
     */
    _emit(event, data) {
        const callbacks = this._callbacks[event] || [];
        callbacks.forEach(cb => cb(data));
    }

    /**
     * Disconnect all providers
     */
    async disconnectAll() {
        for (const [name, provider] of this._providers) {
            try {
                await provider.disconnect();
            } catch (e) {
                console.warn(`Error disconnecting ${name}:`, e);
            }
        }
        this._activeProvider = null;
        this._activeProviderName = null;
    }

    /**
     * Check if any provider supports streaming
     */
    hasStreamingProvider() {
        for (const [name, provider] of this._providers) {
            if (provider.supportsStreaming()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if active provider supports streaming
     */
    activeProviderSupportsStreaming() {
        return this._activeProvider?.supportsStreaming() || false;
    }
}

// Export singleton factory
let _instance = null;

export function getSwapProviderManager(config = {}) {
    if (!_instance) {
        _instance = new SwapProviderManager(config);
    }
    return _instance;
}

export function resetSwapProviderManager() {
    if (_instance) {
        _instance.disconnectAll();
        _instance = null;
    }
}

export default SwapProviderManager;
